"use client";
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { FocusTrap } from "focus-trap-react";
import { cn } from "../utils/cn.js";
import { Icon } from "./Icon.js";
const SIZE = {
    sm: "max-w-[480px]",
    md: "max-w-[640px]",
    lg: "max-w-[820px]",
};
/**
 * Modal — focus-trapped dialog with ESC/backdrop close, scroll-lock,
 * sm/md/lg sizes, backdrop blur, optional header + footer slots.
 *
 * Renders into `document.body` via createPortal. SSR-safe: returns null
 * when `open` is false OR when running outside a browser.
 */
export function Modal({ open, onClose, title, size = "md", closeOnBackdrop = true, closeOnEsc = true, initialFocus, children, footer, className, ...rest }) {
    const overlayRef = useRef(null);
    useEffect(() => {
        if (!open)
            return;
        const prev = document.body.style.overflow;
        document.body.style.overflow = "hidden";
        const onKey = (e) => {
            if (e.key === "Escape" && closeOnEsc)
                onClose();
        };
        window.addEventListener("keydown", onKey);
        return () => {
            document.body.style.overflow = prev;
            window.removeEventListener("keydown", onKey);
        };
    }, [open, closeOnEsc, onClose]);
    if (!open)
        return null;
    if (typeof document === "undefined")
        return null;
    const labelledById = title ? "wm-modal-title" : undefined;
    return createPortal(_jsx(FocusTrap, { focusTrapOptions: {
            initialFocus,
            escapeDeactivates: false,
            allowOutsideClick: true,
        }, children: _jsx("div", { ref: overlayRef, "data-slot": "modal-overlay", role: "dialog", "aria-modal": "true", "aria-labelledby": labelledById, className: "fixed inset-0 z-50 flex items-center justify-center bg-ink-900/40 backdrop-blur-md p-4", onMouseDown: (e) => {
                if (closeOnBackdrop && e.target === overlayRef.current)
                    onClose();
            }, ...rest, children: _jsxs("div", { "data-slot": "modal-panel", "data-size": size, className: cn("w-full overflow-hidden rounded-sm border border-ink-200 bg-ivory shadow-3", SIZE[size], className), children: [title ? (_jsxs("header", { "data-slot": "modal-header", className: "flex items-center justify-between gap-4 border-b border-ink-200 px-6 py-4", children: [_jsx("h2", { id: labelledById, className: "font-serif text-title-lg text-ink-900", children: title }), _jsx("button", { type: "button", "aria-label": "Close", onClick: onClose, className: "rounded-sm p-1 text-ink-500 hover:bg-accent-soft focus-visible:shadow-focus outline-none", children: _jsx(Icon, { name: "x", size: 18, "aria-hidden": true }) })] })) : null, _jsx("div", { "data-slot": "modal-body", className: "px-6 py-5", children: children }), footer ? (_jsx("footer", { "data-slot": "modal-footer", className: "flex items-center justify-end gap-3 border-t border-ink-200 bg-paper-warm px-6 py-4", children: footer })) : null] }) }) }), document.body);
}
